import { useState, useEffect } from "react";
import { getAuth, signOut } from "firebase/auth";
import { useTheme } from "./ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./Dashboard.module.css";

export default function Dashboard({ user, firstName, showEmail, setShowEmail, children, sidebarCollapsed, setSidebarCollapsed, setShowSpotlight }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Use props if provided, otherwise use local state for backward compatibility
  const [localSidebarCollapsed, setLocalSidebarCollapsed] = useState(false);
  const isCollapsed = sidebarCollapsed !== undefined ? sidebarCollapsed : localSidebarCollapsed;
  const setCollapsed = setSidebarCollapsed || setLocalSidebarCollapsed;
  
  const { isDarkMode, toggleTheme } = useTheme();
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get current active page from location
  const getActivePage = () => {
    const path = location.pathname;
    if (path === "/" || path === "/home") return "home";
    if (path.startsWith("/products")) return "products";
    if (path.startsWith("/combos")) return "combos";
    if (path.startsWith("/ingredient-history")) return "ingredient-history";
    if (path.startsWith("/ingredient-usage")) return "ingredient-usage";
    if (path.startsWith("/ingredients")) return "ingredients";
    if (path.startsWith("/stores")) return "stores";
    if (path.startsWith("/items")) return "items";
    if (path.startsWith("/vendors")) return "vendors";
    if (path.startsWith("/inventory")) return "inventory";
    if (path.startsWith("/production-predictor")) return "production-predictor";
    if (path.startsWith("/production")) return "production";
    if (path.startsWith("/production-history")) return "production-history";
    if (path.startsWith("/analytics")) return "analytics";
    return "home";
  };
  
  const activePage = getActivePage();

  // Reset scroll to top when switching pages
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className={styles.appWrapper}>
      {/* Desktop Sidebar */}
      <aside className={`${styles.sidebar} ${isCollapsed ? styles.sidebarCollapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          {isCollapsed ? (
            <button
              className={styles.sidebarToggle}
              onClick={() => setCollapsed(!isCollapsed)}
              aria-label="Expand sidebar"
            >
              →
            </button>
          ) : (
            <>
              <div className={styles.sidebarLogo}>
                <span className={styles.logoIcon}>📦</span>
                <span className={styles.logoText}>Product Manager</span>
              </div>
              <button
                className={styles.sidebarToggle}
                onClick={() => setCollapsed(!isCollapsed)}
                aria-label="Collapse sidebar"
              >
                ←
              </button>
            </>
          )}
        </div>

        {/* Welcome and Search Section */}
        <div className={styles.sidebarTopSection}>
          {/* User Welcome */}
          <div className={styles.sidebarWelcome}>
            <div className={styles.welcomeAvatar}>
              <span className={styles.avatarIcon}>👤</span>
            </div>
            {!isCollapsed && (
              <div className={styles.welcomeContent}>
                <div className={styles.welcomeGreeting}>Welcome back!</div>
                <div 
                  onClick={() => setShowEmail && setShowEmail(!showEmail)}
                  className={styles.welcomeName}
                  title="Click to toggle between name and email"
                >
                  {showEmail ? user.email : firstName}
                </div>
              </div>
            )}
          </div>
          
          {/* Search Button */}
          <button
            onClick={() => setShowSpotlight && setShowSpotlight(true)}
            className={`${styles.sidebarSearchButton} ${isCollapsed ? styles.searchButtonCollapsed : ''}`}
            title="Search (Cmd+K)"
          >
            <span className={styles.searchIcon}>🔍</span>
            {!isCollapsed && (
              <>
                <span className={styles.searchText}>Search</span>
                <span className={styles.searchShortcut}>⌘K</span>
              </>
            )}
          </button>
        </div>
        
        <nav className={styles.sidebarNav}>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "home" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/home")}
            title="Home"
          >
            <span className={styles.navIcon} role="img" aria-label="home">🏠</span>
            {!isCollapsed && <span className={styles.navText}>Home</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "products" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/products")}
            title="Products"
          >
            <span className={styles.navIcon} role="img" aria-label="products">📦</span>
            {!isCollapsed && <span className={styles.navText}>Products</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "combos" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/combos")}
            title="Combos"
          >
            <span className={styles.navIcon} role="img" aria-label="combos">🧺</span>
            {!isCollapsed && <span className={styles.navText}>Combos</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "ingredients" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/ingredients")}
            title="Ingredients"
          >
            <span className={styles.navIcon} role="img" aria-label="ingredients">🥕</span>
            {!isCollapsed && <span className={styles.navText}>Ingredients</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "ingredient-history" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/ingredient-history")}
            title="Ingredient Purchase History"
          >
            <span className={styles.navIcon} role="img" aria-label="history">🧾</span>
            {!isCollapsed && <span className={styles.navText}>Ingredient History</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "ingredient-usage" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/ingredient-usage")}
            title="Ingredient Usage History"
          >
            <span className={styles.navIcon} role="img" aria-label="usage">📉</span>
            {!isCollapsed && <span className={styles.navText}>Ingredient Usage</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "stores" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/stores")}
            title="Stores"
          >
            <span className={styles.navIcon} role="img" aria-label="stores">🏬</span>
            {!isCollapsed && <span className={styles.navText}>Stores</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "items" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/items")}
            title="Items"
          >
            <span className={styles.navIcon} role="img" aria-label="items">📦</span>
            {!isCollapsed && <span className={styles.navText}>Items</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "vendors" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/vendors")}
            title="Vendors"
          >
            <span className={styles.navIcon} role="img" aria-label="vendors">🏢</span>
            {!isCollapsed && <span className={styles.navText}>Vendors</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "inventory" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/inventory")}
            title="Inventory Overview"
          >
            <span className={styles.navIcon} role="img" aria-label="inventory">📊</span>
            {!isCollapsed && <span className={styles.navText}>Inventory</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "production" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/production")}
            title="Production"
          >
            <span className={styles.navIcon} role="img" aria-label="production">🏭</span>
            {!isCollapsed && <span className={styles.navText}>Production</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "production-predictor" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/production-predictor")}
            title="Production Predictor"
          >
            <span className={styles.navIcon} role="img" aria-label="predictor">🔮</span>
            {!isCollapsed && <span className={styles.navText}>Predictor</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "production-history" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/production-history")}
            title="Production History"
          >
            <span className={styles.navIcon} role="img" aria-label="production-history">🗂️</span>
            {!isCollapsed && <span className={styles.navText}>Production History</span>}
          </button>
          <button
            className={`${styles.sidebarNavButton} ${activePage === "analytics" ? styles.activeSidebarNav : ""}`}
            onClick={() => navigate("/analytics")}
            title="Analytics"
          >
            <span className={styles.navIcon} role="img" aria-label="analytics">📈</span>
            {!isCollapsed && <span className={styles.navText}>Analytics</span>}
          </button>
        </nav>
        
        <div className={styles.sidebarFooter}>
          <button
            onClick={toggleTheme}
            className={styles.sidebarThemeToggle}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <span className={styles.themeIcon}>{isDarkMode ? "☀️" : "🌙"}</span>
            {!isCollapsed && <span className={styles.themeText}>{isDarkMode ? "Light Mode" : "Dark Mode"}</span>}
          </button>
          
          <button
            onClick={() => signOut(auth)}
            className={styles.sidebarLogoutButton}
            title="Logout"
          >
            <span className={styles.logoutIcon} role="img" aria-label="logout">🚪</span>
            {!isCollapsed && <span className={styles.logoutText}>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Navigation Toggle */}
      <div className={styles.mobileNavToggle}>
        <button 
          className={styles.hamburgerButton}
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label="Toggle navigation"
        >
          <span className={`${styles.hamburgerLine} ${mobileNavOpen ? styles.hamburgerOpen : ''}`}></span>
          <span className={`${styles.hamburgerLine} ${mobileNavOpen ? styles.hamburgerOpen : ''}`}></span>
          <span className={`${styles.hamburgerLine} ${mobileNavOpen ? styles.hamburgerOpen : ''}`}></span>
        </button>
      </div>

      {/* Navigation Bar */}
      <div className={`${styles.navBar} ${mobileNavOpen ? styles.navBarOpen : styles.navBarClosed}`}>
        <button
          className={`${styles.navButton} ${activePage === "home" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/home");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="home">🏠</span> Home
        </button>
        <button
          className={`${styles.navButton} ${activePage === "products" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/products");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="products">📦</span> Products
        </button>
        <button
          className={`${styles.navButton} ${activePage === "combos" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/combos");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="combos">🧺</span> Combos
        </button>
        <button
          className={`${styles.navButton} ${activePage === "ingredients" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/ingredients");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="ingredients">🥕</span> Ingredients
        </button>
        <button
          className={`${styles.navButton} ${activePage === "ingredient-history" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/ingredient-history");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="history">🧾</span> Ingredient History
        </button>
        <button
          className={`${styles.navButton} ${activePage === "ingredient-usage" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/ingredient-usage");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="usage">📉</span> Ingredient Usage
        </button>
        <button
          className={`${styles.navButton} ${activePage === "stores" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/stores");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="stores">🏬</span> Stores
        </button>
        <button
          className={`${styles.navButton} ${activePage === "items" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/items");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="items">📦</span> Items
        </button>
        <button
          className={`${styles.navButton} ${activePage === "vendors" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/vendors");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="vendors">🏢</span> Vendors
        </button>
        <button
          className={`${styles.navButton} ${activePage === "inventory" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/inventory");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="inventory">📊</span> Inventory
        </button>
        <button
          className={`${styles.navButton} ${activePage === "production" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/production");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="production">🏭</span> Production
        </button>
        <button
          className={`${styles.navButton} ${activePage === "production-predictor" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/production-predictor");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="predictor">🔮</span> Predictor
        </button>
        <button
          className={`${styles.navButton} ${activePage === "production-history" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/production-history");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="production-history">🗂️</span> Production History
        </button>
        <button
          className={`${styles.navButton} ${activePage === "analytics" ? styles.activeNav : ""}`}
          onClick={() => {
            navigate("/analytics");
            setMobileNavOpen(false);
          }}
        >
          <span role="img" aria-label="analytics">📈</span> Analytics
        </button>
        
        {/* User Info Section (Mobile Only) */}
        <div className={styles.mobileUserInfo}>
          <div className={styles.mobileUserName}>
            <span role="img" aria-label="user">👤</span>
            <span 
              onClick={() => setShowEmail && setShowEmail(!showEmail)}
              className={styles.userName}
              title="Click to toggle between name and email"
            >
              {showEmail ? user.email : firstName}
            </span>
          </div>
          <button
            onClick={() => signOut(auth)}
            className={styles.mobileLogoutButton}
          >
            <span role="img" aria-label="logout">🚪</span> Logout
          </button>
        </div>
        
        {/* Theme Toggle in Mobile Menu */}
        <button 
          onClick={() => {
            toggleTheme();
            setMobileNavOpen(false);
          }}
          className={styles.mobileThemeToggleInMenu}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? "☀️" : "🌙"} {isDarkMode ? "Light Mode" : "Dark Mode"}
        </button>
      </div>
      
      {/* Mobile Overlay */}
      {mobileNavOpen && (
        <div 
          className={styles.mobileOverlay}
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      
      {/* Scrollable Content Area */}
      <div className={`${styles.contentWrapper} ${isCollapsed ? styles.contentWrapperCollapsed : styles.contentWrapperExpanded}`}>
        {children}
      </div>
    </div>
  );
}
